import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { LINK_ICONS, ICON_OPTIONS } from "@/constants/linkIcons";

const BLANK_LINK = { label: "", url: "", icon: "link", order: 0 };

export function QuickLinksTab() {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_LINK);
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => api.get("/quicklinks").then((r) => setLinks(r.data)).catch((e) => console.error("quicklinks load", e)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm({ ...BLANK_LINK, order: links.length + 1 }); setOpen(true); };
  const openEdit = (l) => { setEditId(l.id); setForm({ label: l.label || "", url: l.url || "", icon: l.icon || "link", order: l.order ?? 0 }); setOpen(true); };
  const save = async () => {
    if (!form.label || !form.url) return toast.error("Label and URL required");
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editId) { await api.put(`/quicklinks/${editId}`, payload); toast.success("Link updated"); }
      else { await api.post("/quicklinks", payload); toast.success("Link added"); }
      setOpen(false); setForm(BLANK_LINK); setEditId(null); load();
    } catch (e) { console.error("save link", e); toast.error("Could not save link"); }
  };
  const remove = async (id) => { try { await api.delete(`/quicklinks/${id}`); load(); toast.success("Link removed"); } catch (e) { console.error("delete link", e); toast.error("Error"); } };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Add, edit, reorder and remove the quick links shown on the Home page. Pick an icon and set the order.</p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditId(null); setForm(BLANK_LINK); } }}>
          <DialogTrigger asChild><Button data-testid="add-link-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> New Link</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editId ? "Edit Quick Link" : "New Quick Link"}</DialogTitle><DialogDescription className="sr-only">Quick link form.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Label</Label><Input data-testid="link-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Time Entry" /></div>
              <div><Label className="text-xs">URL</Label><Input data-testid="link-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://… or /internal-path" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Icon</Label>
                  <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                    <SelectTrigger data-testid="link-icon"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {ICON_OPTIONS.map((name) => {
                        const Ico = LINK_ICONS[name];
                        return <SelectItem key={name} value={name}><span className="flex items-center gap-2"><Ico className="h-4 w-4 text-primary" /> {name}</span></SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Order</Label><Input data-testid="link-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="save-link-btn" onClick={save}>{editId ? "Save Changes" : "Add Link"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-border rounded-sm overflow-hidden" data-testid="links-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60"><TableHead className="w-16">Icon</TableHead><TableHead>Label</TableHead><TableHead>URL</TableHead><TableHead className="w-16">Order</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {links.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No quick links yet.</TableCell></TableRow>}
            {links.map((l) => {
              const Ico = LINK_ICONS[l.icon] || ArrowRight;
              return (
                <TableRow key={l.id} data-testid="link-row">
                  <TableCell><span className="grid place-items-center h-8 w-8 bg-primary/10 text-primary rounded-sm"><Ico className="h-4 w-4" /></span></TableCell>
                  <TableCell className="font-medium">{l.label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{l.url}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.order}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <button data-testid="edit-link-btn" onClick={() => openEdit(l)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                      <button data-testid="delete-link-btn" onClick={() => remove(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
